import React, { useState, useEffect } from 'react';
import ModalShell from '@/components/ModalShell';
import Avatar from '@/components/Avatar';
import { I } from '@/lib/icons';
import { useWorkspace, useClients } from '@/hooks/useData';
import { useCloseOnWorkspaceSwitch } from '@/contexts/WorkspaceContext';
import { useToast } from '@/components/Toast';

export default function ClientModal({ open, onClose, client }) {
  const { workspace } = useWorkspace();
  const { create, update, remove } = useClients(workspace?.id);
  const toast = useToast();
  const isNew = !client;
  const [form, setForm] = useState({ name: '', ico: '', dic: '', ic_dph: '', address: '', email: '', is_pinned: false });
  const [saving, setSaving] = useState(false);

  useCloseOnWorkspaceSwitch(open, onClose);

  useEffect(() => {
    if (open) {
      if (client) setForm({
        name: client.name || '', ico: client.ico || '', dic: client.dic || '',
        ic_dph: client.ic_dph || '', address: client.address || '',
        email: client.email || '', is_pinned: !!client.is_pinned,
      });
      else setForm({ name: '', ico: '', dic: '', ic_dph: '', address: '', email: '', is_pinned: false });
    }
  }, [open, client]);

  if (!open) return null;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: typeof e === 'object' ? e.target.value : e }));
  const previewName = form.name || 'Nový klient';

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Zadaj názov klienta.'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        ico: form.ico.trim() || null,
        dic: form.dic.trim() || null,
        ic_dph: form.ic_dph.trim() || null,
        address: form.address.trim() || null,
        email: form.email.trim() || null,
        is_pinned: !!form.is_pinned,
      };
      if (isNew) {
        await create(payload);
        toast.success('Klient pridaný.');
      } else {
        await update(client.id, payload);
        toast.success('Uložené.');
      }
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!client) return;
    if (!window.confirm(`Naozaj odstrániť ${client.name}?`)) return;
    setSaving(true);
    try { await remove(client.id); toast.success('Vymazané.'); onClose(); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell open={open} onClose={onClose}
      title={isNew ? 'Nový klient' : 'Detail klienta'} maxWidth={560}
      rightAction={
        <button onClick={submit} disabled={saving}
          data-testid="save-client" className="cd-btn cd-btn-primary cd-btn-sm">
          {saving ? <><span className="cd-spinner" style={{ borderTopColor: '#fff' }} /> Ukladám...</>
            : <><I.check /> Uložiť</>}
        </button>
      }>
      {/* Avatar preview + pin */}
      <div style={{
        padding: '20px 22px 16px', display: 'flex', alignItems: 'center', gap: 14,
        borderBottom: '1px solid var(--border)',
      }}>
        <Avatar name={previewName} size={56} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{previewName}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            Avatar generovaný z initials · farba podľa hash mena
          </div>
        </div>
        <button onClick={() => setForm(f => ({ ...f, is_pinned: !f.is_pinned }))}
          style={{
            background: form.is_pinned ? 'var(--sec-orange-soft)' : 'transparent',
            border: `1px solid ${form.is_pinned ? 'var(--sec-orange)' : 'var(--border)'}`,
            color: form.is_pinned ? 'var(--sec-orange)' : 'var(--text-muted)',
            padding: '7px 12px', borderRadius: 'var(--r-full)',
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}>
          <I.pin /> {form.is_pinned ? 'Pripnutý' : 'Pripnúť'}
        </button>
      </div>

      {/* Form */}
      <div style={{ padding: '18px 22px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Názov firmy" value={form.name} onChange={set('name')} full testId="client-name" />
        <Input label="IČO" value={form.ico} onChange={set('ico')} mono testId="client-ico" />
        <Input label="DIČ" value={form.dic} onChange={set('dic')} mono />
        <Input label="IČ DPH" value={form.ic_dph} onChange={set('ic_dph')} mono full
          placeholder="napr. SK2023456789 (voliteľné)" />
        <Input label="Adresa" value={form.address} onChange={set('address')} full />
        <Input label="Email" value={form.email} onChange={set('email')} full placeholder="info@klient.sk" />
      </div>

      {!isNew && (
        <div style={{
          padding: '10px 22px 18px', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button onClick={onDelete} disabled={saving}
            data-testid="delete-client"
            style={{
              background: 'transparent', border: 'none', color: 'var(--danger)',
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>
            <I.trash /> Odstrániť klienta
          </button>
        </div>
      )}
    </ModalShell>
  );
}

function Input({ label, value, onChange, mono, full, placeholder, testId }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
      }}>{label}</div>
      <input className={`cd-input${mono ? ' mono' : ''}`} data-testid={testId}
        value={value || ''} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}
